import { AffiliateIdMapping, MerchantIdMapping } from '../constants.js';
import { identifyOfferStore } from './offers.js';
/**
 * Accepts offer set, number of desired offers,
 * preferred merchant ids (ex: ['59','9419']]),
 * preferred affiliate ids (ex: ['amazon-affiliate-1']),
 * @todo: Logic currently doesn't handle the case of selecting upon affiliate within a merchant.
 */
export function selectBestOffers(offers, desiredOfferCount = 3, preferredMerchantIds, preferredAffiliateIds) {
    if (offers.length === 0) {
        return [];
    }
    // cloning so our selection doesn't affect the overall offer set
    const offerClone = [...offers];
    if ((!preferredMerchantIds || preferredMerchantIds.length === 0) &&
        (!preferredAffiliateIds || preferredAffiliateIds.length === 0)) {
        return offerClone.slice(0, desiredOfferCount);
    }
    const selectedOfferDetails = [];
    const offerMaps = buildOfferMaps(offers);
    if (preferredMerchantIds) {
        selectedOfferDetails.push(...selectAssicatedOffers(desiredOfferCount, preferredMerchantIds, offerMaps.merchantMap, offerClone));
    }
    if (selectedOfferDetails.length >= desiredOfferCount) {
        // just in case we selected more than we were instructed to.
        return selectedOfferDetails.slice(0, desiredOfferCount).map((details) => details.offer);
    }
    // it is important to prune the offer set so we don't accidentally select the same offer twice,
    // or a secont offer from the same merchant that is on a different affiliate network
    pruneOfferSet(offerClone, selectedOfferDetails);
    if (preferredAffiliateIds) {
        selectedOfferDetails.push(...selectAssicatedOffers(desiredOfferCount - selectedOfferDetails.length, preferredAffiliateIds, offerMaps.affiliateMap, offerClone));
    }
    const result = selectedOfferDetails.map((details) => details.offer);
    if (selectedOfferDetails.length < desiredOfferCount) {
        // prune before grabbing a few to fill in the last spaces
        pruneOfferSet(offerClone, selectedOfferDetails);
        const finalOffers = [...offerClone].slice(0, desiredOfferCount - selectedOfferDetails.length);
        result.push(...finalOffers);
    }
    return result;
}
/**
 * Select offers from supplied set
 *
 * We do so bychecking if the map ids (merchant or associate)
 * @param desiredOfferCount how many offers do we want to select from the set
 * @param preferredMapIds an ordered list of ids describing which merchants or affiliates to select from first
 * @param associatedMap a map of merchant or affiliate ids to offer indexes.
 * @param allOffers a (probably sparse) array of CommerceOffers to select from
 */
function selectAssicatedOffers(desiredOfferCount, preferredMapIds, associatedMap, allOffers) {
    const selectedOffers = [];
    let mapIndex = 0;
    let offerCount = 0;
    while (offerCount < desiredOfferCount && mapIndex < preferredMapIds.length) {
        // get the offer ids associated with the current prefe
        const associatedOfferIndexes = associatedMap.get(preferredMapIds[mapIndex]);
        const selected = selectAssociatedOffer(associatedOfferIndexes, allOffers);
        if (selected !== undefined) {
            offerCount = selectedOffers.push(selected);
        }
        mapIndex++;
    }
    return selectedOffers;
}
/**
 * Use the supplied offer indexes to select an offer from a full offer set
 */
function selectAssociatedOffer(associatedOfferIndexes, allOffers) {
    if (associatedOfferIndexes === undefined || associatedOfferIndexes.length === 0) {
        return undefined;
    }
    // For now, just returning the first offer we find for the key
    const selectedIndex = associatedOfferIndexes.find((offerIndex) => allOffers[offerIndex] !== undefined);
    if (selectedIndex === undefined) {
        return undefined;
    }
    return {
        offer: allOffers[selectedIndex],
        pruneIndexes: associatedOfferIndexes,
    };
}
/**
 * Look up a merchant id by merchant name (Squirrel only for now)
 */
function lookupMerchantId(merchant) {
    if (merchant === undefined) {
        return undefined;
    }
    const merchantIds = Object.keys(MerchantIdMapping);
    return merchantIds.find((k) => {
        return MerchantIdMapping[k].toLocaleLowerCase() === merchant;
    });
}
/**
 * Look up an affiliate Id by the affiliate name (Squirrel only for now)
 */
function lookupAffiliateId(affiliate) {
    if (affiliate === undefined) {
        return undefined;
    }
    const affiliates = Object.keys(AffiliateIdMapping);
    return affiliates.find((k) => AffiliateIdMapping[k].toLocaleLowerCase() === affiliate);
}
/**
 * Build a map of offers to their merchant and affiliate for use in selecting the best offer
 * @todo would be nice to have some logging of missing merchants
 * @see selectBestOffers
 * @returns [merchantMap, affiliateMap]
 */
function buildOfferMaps(offers) {
    const merchantMap = new Map();
    const affiliateMap = new Map();
    const addToMap = (map, key, addition) => {
        const existing = map.get(key) ?? [];
        existing.push(addition);
        map.set(key, existing);
    };
    for (let offerIndex = 0; offerIndex < offers.length; offerIndex++) {
        const merchant = identifyOfferStore(offers[offerIndex])?.toLocaleLowerCase();
        const merchantId = lookupMerchantId(merchant);
        if (merchantId !== undefined) {
            addToMap(merchantMap, merchantId, offerIndex);
        }
        const affiliateId = lookupAffiliateId(offers[offerIndex].vendor.toLocaleLowerCase());
        if (affiliateId !== undefined) {
            addToMap(affiliateMap, affiliateId, offerIndex);
        }
    }
    return { merchantMap, affiliateMap };
}
/**
 * removes selected offers (and any associated offers) from an offer set.
 *
 * @param allOffers this array is mutated in place, not returned!
 * @param selected the offer details to use for pruning
 */
function pruneOfferSet(allOffers, selected) {
    selected
        .map((offerDetails) => offerDetails.pruneIndexes)
        .flat()
        .forEach((offerIndex) => delete allOffers[offerIndex]);
}
//# sourceMappingURL=offerSelection.js.map